import { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { CalendarPlus, UserCheck, X, AlertTriangle } from 'lucide-react';
import { useI18n } from '../../context/useI18n';
import api from '../../services/api';
import { ADMIN_FORM_MODAL_PANEL } from './adminModalPanel';
import { hasSedeFinalRole } from '../../utils/gymRoleScope';
import { isFoodOnlyDomainUser } from '../../utils/adminUserScopes';

const DAY_PRESETS = [7, 15, 30, 60, 90];

function remainingVigenciaDays(user) {
  const sub = user?.subscription || {};
  const fromSub = Number(sub.days_left);
  if (Number.isFinite(fromSub) && fromSub >= 1 && fromSub <= 365) return Math.ceil(fromSub);
  const venc = sub.fecha_vencimiento || sub.payment_due_date;
  const inicio = sub.fecha_inicio || sub.cycle_start_date || null;
  if (venc) {
    const endMs = new Date(venc).getTime();
    const startMs = inicio ? new Date(inicio).getTime() : null;
    // Antes del inicio (ciclo adelantado): ventana completa. Si ya inició: hoy→fin.
    const left = (startMs && startMs > Date.now())
      ? Math.ceil((endMs - startMs) / 86400000)
      : Math.ceil((endMs - Date.now()) / 86400000);
    if (Number.isFinite(left) && left >= 1 && left <= 365) return left;
  }
  const licUntil = (user?.licenses || [])
    .filter((l) => ['d28d', 'gym', 'live_classes', 'training', 'food', 'food_plan', 'nutrition'].includes(String(l.module_code || '')))
    .map((l) => l.valid_until)
    .filter(Boolean)
    .map((d) => new Date(d).getTime())
    .filter((n) => Number.isFinite(n));
  if (licUntil.length) {
    const left = Math.ceil((Math.max(...licUntil) - Date.now()) / 86400000);
    if (Number.isFinite(left) && left >= 1 && left <= 365) return left;
  }
  return 30;
}

function resolveDefaultActivateDays(user) {
  if (user?.activate_days != null) {
    const n = Number(user.activate_days);
    if (Number.isFinite(n) && n >= 1 && n <= 365) return String(Math.ceil(n));
  }
  return String(remainingVigenciaDays(user));
}

function resolveInitialCycleId(user) {
  const raw = user?.subscription?.cycle_id ?? user?.cycle_id ?? null;
  if (raw == null || raw === '') return '';
  return String(raw);
}

/** Solo consumidor sede (no operadores con gym_parent_id). */
function isSedeVigenciaTarget(user) {
  if (!user) return false;
  if (user.is_sede_final === true) return true;
  if (hasSedeFinalRole(user)) return true;
  return user.rol === 'usuario_final_sede';
}

/** Food Plan self-serve: solo vigencia food; sin ciclos D28D ni cambio de rol. */
function isFoodVigenciaTarget(user, plan) {
  if (!user && !plan) return false;
  if (user && isFoodOnlyDomainUser(user)) return true;
  const kind = String(
    plan?.kind || user?.subscription?.kind || user?.subscription?.plan_kind || user?.plan_kind || '',
  ).toLowerCase();
  if (kind === 'food') return true;
  const planName = String(
    plan?.nombre || user?.subscription?.plan || user?.planId || user?.plan || '',
  ).trim();
  return /^FOOD/i.test(planName);
}

export default function UserActivateModal({
  open,
  user = null,
  plan = null,
  saving = false,
  error = '',
  allowFinanceZero = false,
  onClose,
  onConfirm,
}) {
  const { t } = useI18n();
  const [days, setDays] = useState('30');
  const [cycleId, setCycleId] = useState('');
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState('');
  const [financeZero, setFinanceZero] = useState(false);

  const planName = plan?.nombre || user?.subscription?.plan || user?.planId || '';
  const sedeVigenciaOnly = isSedeVigenciaTarget(user);
  const foodVigenciaOnly = !sedeVigenciaOnly && isFoodVigenciaTarget(user, plan);
  // No inferir D28D solo por module_access contaminado (ancla gym) en usuarios Food.
  const isD28dPlan = !sedeVigenciaOnly && !foodVigenciaOnly && (
    String(plan?.kind || '').toLowerCase() === 'd28d'
    || (
      !plan?.kind
      && Boolean(user?.module_access?.d28d || user?.module_access?.live_classes)
    )
  );
  const savedCycleId = resolveInitialCycleId(user);

  const cycleOptions = useMemo(() => {
    const allowed = Array.isArray(preview?.allowed_cycles) ? preview.allowed_cycles : [];
    const base = allowed.length
      ? allowed
      : [preview?.active_cycle, preview?.cycle].filter(Boolean);
    const byId = new Map();
    for (const c of base) {
      if (c?.id != null) byId.set(Number(c.id), c);
    }
    // Asegurar que el ciclo ya guardado en la cuenta aparezca en el select.
    if (savedCycleId && !byId.has(Number(savedCycleId))) {
      const fromPreview = preview?.cycle && Number(preview.cycle.id) === Number(savedCycleId)
        ? preview.cycle
        : null;
      byId.set(Number(savedCycleId), fromPreview || {
        id: Number(savedCycleId),
        name: `Ciclo ${savedCycleId}`,
        startDate: null,
      });
    }
    return [...byId.values()];
  }, [preview, savedCycleId]);

  const loadPreview = useCallback(async () => {
    if (!open || !isD28dPlan || !planName || sedeVigenciaOnly || foodVigenciaOnly) {
      setPreview(null);
      setPreviewError('');
      return;
    }
    setPreviewLoading(true);
    setPreviewError('');
    try {
      const gymId = user?.gym_id ?? user?.gymId ?? '';
      const preferredCycle = cycleId || savedCycleId;
      const q = new URLSearchParams({
        plan: planName,
        ...(gymId ? { gym_id: String(gymId) } : {}),
        ...(preferredCycle ? { cycle_id: String(preferredCycle) } : {}),
      });
      const res = await api.get(`/d28d/cycle/enrollment-preview?${q.toString()}`);
      const data = res.data?.data || null;
      setPreview(data);
      // Prefijar con ciclo de la cuenta; si no hay, usar el del preview (automático).
      if (!cycleId) {
        const fromAccount = savedCycleId;
        const fromPreview = data?.cycle_id != null ? String(data.cycle_id) : '';
        if (fromAccount) setCycleId(fromAccount);
        else if (fromPreview) setCycleId(fromPreview);
      } else if (
        data?.cycle_id != null
        && savedCycleId
        && Number(cycleId) === Number(savedCycleId)
        && Number(data.cycle_id) !== Number(cycleId)
      ) {
        // Remapeo plataforma → gym en el preview: alinear el select.
        setCycleId(String(data.cycle_id));
      }
    } catch (err) {
      setPreview(null);
      setPreviewError(err?.response?.data?.error || t('users.cycle_preview_error', 'No se pudo cargar los ciclos disponibles.'));
    } finally {
      setPreviewLoading(false);
    }
  }, [open, isD28dPlan, planName, user, cycleId, savedCycleId, sedeVigenciaOnly, foodVigenciaOnly, t]);

  useEffect(() => {
    if (open) {
      setDays(resolveDefaultActivateDays(user));
      setCycleId(resolveInitialCycleId(user));
      setPreview(null);
      setPreviewError('');
      setFinanceZero(false);
    }
  }, [open, user, user?.id, user?.activate_days, user?.subscription?.days_left, user?.subscription?.fecha_vencimiento, user?.subscription?.cycle_id]);

  useEffect(() => {
    if (!open) return undefined;
    const timer = setTimeout(() => { loadPreview(); }, 50);
    return () => clearTimeout(timer);
  }, [open, loadPreview, planName]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape' && !saving) onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, saving, onClose]);

  if (!open || !user) return null;

  const parsedDays = Number(days);
  const valid = Number.isFinite(parsedDays) && parsedDays >= 1 && parsedDays <= 365;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!valid || saving) return;
    onConfirm?.({
      days: parsedDays,
      cycle_id: (sedeVigenciaOnly || foodVigenciaOnly)
        ? null
        : (isD28dPlan && cycleId ? Number(cycleId) : undefined),
      plan: planName || undefined,
      finance_zero: allowFinanceZero ? financeZero : false,
    });
  };

  const activeCycle = preview?.active_cycle;
  const selectedCycle = preview?.cycle;
  const savedCycleLabel = cycleOptions.find((c) => Number(c.id) === Number(cycleId))
    || (cycleId ? { name: `Ciclo ${cycleId}` } : null);

  const modal = (
    <div
      className="form-modal-overlay"
      style={{ zIndex: 10050 }}
      role="dialog"
      aria-modal="true"
      onClick={() => !saving && onClose?.()}
    >
      <div
        className={`${ADMIN_FORM_MODAL_PANEL} max-w-lg`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="form-modal-header">
          <div className="flex items-center gap-2 min-w-0">
            <span className="inline-flex items-center justify-center w-9 h-9 rounded-xl bg-emerald-500/15 text-emerald-700 shrink-0">
              <UserCheck size={18} />
            </span>
            <div className="min-w-0">
              <h4 className="font-semibold text-stone-900 truncate">
                {t('users.activate_user', 'Activar usuario')}
              </h4>
              <p className="text-xs text-stone-500 truncate">{user.nombre}</p>
            </div>
          </div>
          <button
            type="button"
            className="form-close-btn"
            onClick={onClose}
            disabled={saving}
            aria-label={t('common.close', 'Cerrar')}
          >
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="form-modal-content space-y-4">
          {error ? <div className="alert-error text-sm">{error}</div> : null}

          {sedeVigenciaOnly ? (
            <div className="ui-light-panel rounded-2xl border border-slate-200 p-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-stone-500">
                {t('users.sede_vigencia_only', 'Acceso por vigencia')}
              </p>
              <p className="text-sm text-stone-700 mt-1">
                {t(
                  'users.sede_activate_hint',
                  'Los usuarios de sede no usan ciclos D28D. Al activar, el perfil quedará habilitado solo por los días de vigencia que indiques.',
                )}
              </p>
            </div>
          ) : null}

          {foodVigenciaOnly ? (
            <div className="ui-light-panel rounded-2xl border border-slate-200 p-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-stone-500">
                {t('users.food_vigencia_only', 'Acceso Food Plan')}
              </p>
              <p className="text-sm text-stone-700 mt-1">
                {t(
                  'users.food_activate_hint',
                  'Este usuario es de Food Plan. Al activar se habilita solo Food por los días indicados: no se asigna ciclo D28D ni se cambia el rol.',
                )}
              </p>
            </div>
          ) : null}

          {isD28dPlan && planName && (
            <div className="ui-light-panel rounded-2xl border border-slate-200 p-3 space-y-3">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-stone-500">
                  {t('users.cycle_enrollment', 'Ciclo D28D')}
                </p>
                {previewLoading ? (
                  <p className="text-sm text-stone-500 mt-1">{t('common.loading', 'Cargando…')}</p>
                ) : previewError ? (
                  <p className="text-sm text-red-700 mt-1">{previewError}</p>
                ) : (
                  <>
                    {activeCycle && (
                      <p className="text-sm text-stone-700 mt-1">
                        {t('users.cycle_current', 'Ciclo vigente')}:{' '}
                        <strong>{activeCycle.name}</strong>
                        {activeCycle.day != null ? ` · Día ${activeCycle.day} de ${activeCycle.total_days || 28}` : ''}
                      </p>
                    )}
                    {savedCycleId ? (
                      <p className="text-xs text-stone-500 mt-1">
                        {t('users.cycle_saved_hint', 'Ciclo guardado en cuenta')}:{' '}
                        <strong>
                          {cycleOptions.find((c) => Number(c.id) === Number(savedCycleId))?.name
                            || `Ciclo ${savedCycleId}`}
                        </strong>
                      </p>
                    ) : null}
                    {cycleOptions.length > 0 ? (
                      <select
                        className="input mt-2 w-full"
                        value={cycleId}
                        onChange={(e) => setCycleId(e.target.value)}
                        disabled={saving}
                      >
                        {!savedCycleId ? (
                          <option value="">{t('users.cycle_auto', 'Asignar automáticamente')}</option>
                        ) : null}
                        {cycleOptions.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.name}{c.startDate || c.start_date ? ` (${c.startDate || c.start_date})` : ''}
                          </option>
                        ))}
                      </select>
                    ) : selectedCycle || savedCycleLabel ? (
                      <p className="text-sm text-stone-800 mt-1">
                        {t('users.cycle_assigned', 'Ciclo asignado')}:{' '}
                        <strong>{(selectedCycle || savedCycleLabel).name}</strong>
                      </p>
                    ) : null}
                  </>
                )}
              </div>
              {preview?.late_enrollment && (
                <p className="text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2">
                  {t('users.cycle_late_note', 'El ciclo actual va avanzado (más de 10 días). Se inscribirá en el siguiente ciclo.')}
                </p>
              )}
              {preview?.deferred && (
                <div className="flex gap-2 text-xs text-sky-900 bg-sky-50 border border-sky-200 rounded-xl px-3 py-2">
                  <AlertTriangle size={16} className="shrink-0 mt-0.5" />
                  <p>
                    {preview.message || t('users.cycle_deferred_note', 'Sus servicios se habilitarán un día antes de la fecha de inicio del ciclo inscrito.')}
                    {preview.deferred_until ? ` (${new Date(`${preview.deferred_until}T12:00:00`).toLocaleDateString('es-CO')})` : ''}
                  </p>
                </div>
              )}
            </div>
          )}

          <div>
            <label className="label">{t('vigencias.days_quick', 'Días rápidos')}</label>
            <div className="flex flex-wrap gap-2 mt-1">
              {DAY_PRESETS.map((preset) => {
                const selected = days === String(preset);
                return (
                  <button
                    key={preset}
                    type="button"
                    disabled={saving}
                    className={`min-w-[3rem] px-3 py-1.5 rounded-xl text-sm font-medium ${
                      selected ? 'btn-primary' : 'btn-secondary'
                    }`}
                    onClick={() => setDays(String(preset))}
                  >
                    {preset}d
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <label className="label" htmlFor="activate-days-input">
              {t('vigencias.days_custom', 'Días personalizados')}
            </label>
            <input
              id="activate-days-input"
              type="number"
              min={1}
              max={365}
              className="input w-full"
              value={days}
              disabled={saving}
              onChange={(e) => setDays(e.target.value)}
            />
            {user?.subscription?.days_left != null && Number(user.subscription.days_left) >= 1 ? (
              <p className="text-xs text-stone-500 mt-1">
                {t(
                  'users.activate_days_from_remaining',
                  'Se prellenó con los {n} días restantes de la vigencia anterior.',
                ).replace('{n}', String(Math.ceil(Number(user.subscription.days_left))))}
              </p>
            ) : null}
          </div>

          {allowFinanceZero ? (
            <label className="flex items-start gap-2 text-sm text-stone-800 cursor-pointer ui-light-panel rounded-2xl border border-slate-200 p-3">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={financeZero}
                disabled={saving}
                onChange={(e) => setFinanceZero(e.target.checked)}
              />
              <span>
                <span className="font-medium">
                  {t('users.activate_finance_zero', 'Sin ingreso ($0)')}
                </span>
                <span className="block text-xs text-stone-500 mt-0.5">
                  {t(
                    'users.activate_finance_zero_hint',
                    'Solo super_admin. Staff, cortesía o prueba: no suma en Finanzas. En el detalle aparece como [sin ingreso] o [staff].',
                  )}
                </span>
              </span>
            </label>
          ) : null}

          <div className="form-modal-footer -mx-5 -mb-5 mt-2">
            <button type="button" className="btn-secondary" onClick={onClose} disabled={saving}>
              {t('common.cancel', 'Cancelar')}
            </button>
            <button type="submit" className="btn-primary inline-flex items-center gap-2" disabled={!valid || saving}>
              <CalendarPlus size={16} />
              {saving ? t('common.saving', 'Guardando...') : t('users.activate_user', 'Activar')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  if (typeof document === 'undefined') return modal;
  return createPortal(modal, document.body);
}
